import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { AutoField, AutoFields, AutoForm, SubmitField, useForm } from '@bitstopco/bitstop-theme';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';

import SCHEMAS from '@/forms/schemas';

import {
  AffiliateUserCreationError,
  useAffiliate,
} from '@/store/api/services/affiliates/affiliates';

import type { CreateAffiliateModel } from '@/types';

/**
 * Create Affiliate Modal (v2)
 * Creates the affiliate's first user through athena, which also provisions their auth0 identity
 *
 * @param {ModalChildrenProps} { hide, props }
 * @return {*}
 */
const AffiliateCreateV2 = ({ hide }: ModalChildrenProps) => {
  const navigate = useNavigate();

  const { createAffiliateOrgV2 } = useAffiliate();

  const { schema } = useForm(SCHEMAS.CREATE_AFFILIATE);

  const handleSubmit = async ({
    phone: { phone, dialCode: country_code },
    ...data
  }: CreateAffiliateModel) => {
    try {
      await createAffiliateOrgV2({ ...data, phone, country_code });
      toast.success('Affiliate organization and user created successfully');
      hide();
    } catch (error) {
      if (!(error instanceof AffiliateUserCreationError)) return;

      // Submitting again would create a second organization, so the user is added to this one
      toast.error('Affiliate organization created, but the user could not be added');
      hide();
      navigate(`/affiliate/${error.affiliateId}/users`);
    }
  };

  return (
    <AutoForm<CreateAffiliateModel> showInlineError schema={schema} onSubmit={handleSubmit}>
      <DialogContent sx={{ mt: 1, mb: 2 }} data-test="create-affiliate-v2-modal">
        <Typography variant="h6" mb={2}>
          Affiliate Organization
        </Typography>

        <AutoFields fields={['name', 'description']} />

        <Divider sx={{ my: 3 }} />

        <Stack gap={0.5} mb={2}>
          <Typography variant="h6">Affiliate User Details</Typography>
          <Typography variant="body2">
            This user will have limited viewing access to the sales from machines assigned to the
            organization.
          </Typography>
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <AutoField name="first_name" />
          </Grid>
          <Grid item xs={12} md={6}>
            <AutoField name="last_name" />
          </Grid>
        </Grid>
        <AutoFields fields={['email', 'phone']} />
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={hide}>Cancel</Button>
        <SubmitField data-test="create-affiliate-v2-modal-submit-btn">Create Affiliate</SubmitField>
      </DialogActions>
    </AutoForm>
  );
};

export default AffiliateCreateV2;
